<template>
  <sub-page
    v-if="!hideSalaryView"
    title="Failed salaries"
    subtitle="Employees whose salary is in a failed state, grouped by how long it has been failing."
  >
    <div class="failed-salary-report">
      <page-list-header>
        <template #left>
          <filter-dropdown v-model="filters" :filters="filterItems" />
        </template>
        <template #right>
          <b-form-group label-cols="auto" label="Per page" label-for="failed-salary-report-per-page" class="mb-0">
            <b-form-select
              id="failed-salary-report-per-page"
              v-model="perPage"
              :options="SALARY_REPORT_PER_PAGE_OPTIONS"
            />
          </b-form-group>
          <export-button
            :export-data="filteredRows"
            :export-file-rows="exportFileRows"
            :export-headers="exportHeaders"
            file-name="failed-salary-report"
          />
        </template>
      </page-list-header>

      <div class="mb-3">
        <app-loading :loading="loading" />

        <b-table
          v-if="!loading"
          :items="filteredRows"
          :fields="FAILED_SALARY_REPORT_FIELD_DEFINITIONS"
          :current-page="currentPage"
          :per-page="perPage"
          sort-by="days_in_failed"
          sort-desc
          class="mb-0"
          table-class="table-minimal"
          primary-key="salary_id"
          responsive
          show-empty
          empty-text="No failed salaries found for the selected dates."
        >
          <template #cell(employee_name)="data">
            <router-link :to="{ name: 'employee', params: { employeeId: data.item.employee_id } }">
              {{ data.item.employee_name }}
            </router-link>
          </template>
        </b-table>
      </div>

      <b-pagination
        v-model="currentPage"
        :total-rows="filteredRows.length"
        :per-page="perPage"
        class="justify-content-end"
      />
    </div>
  </sub-page>
</template>

<script setup>
import { startOfMonth, format } from 'date-fns';
import isNil from 'lodash/isNil';
import omitBy from 'lodash/omitBy';
import pick from 'lodash/pick';
import { useQuery } from '@tanstack/vue-query';
import { computed, ref, watch } from 'vue';
import { CONTROL_TYPES } from '@/components/AppInput.vue';
import AppLoading from '@/components/AppLoading.vue';
import ExportButton from '@/components/ExportButton.vue';
import FilterDropdown from '@/components/FilterDropdown.vue';
import PageListHeader from '@/components/PageListHeader.vue';
import SubPage from '@/components/SubPage.vue';
import useFeatureFlags from '@/composables/useFeatureFlags';
import { FAILED_SALARY_REPORT_FIELD_DEFINITIONS, SALARY_REPORT_PER_PAGE_OPTIONS } from '@/pages/reporting/constants';
import { FAILED_SALARY_BUCKET_LABELS, bucketFor, daysInFailed } from '@/pages/reporting/failedSalaryBuckets';
import { getFailedSalaryReport } from '@/queries/failed-salary-report';

const FILTER_KEYS = { STARTS_ON: 'starts_on', ENDS_ON: 'ends_on', BUCKET: 'bucket' };
const API_FILTER_KEYS = [FILTER_KEYS.STARTS_ON, FILTER_KEYS.ENDS_ON];
const STARTS_ON_DEFAULT = format(startOfMonth(new Date()), 'yyyy-MM-dd');
const ENDS_ON_DEFAULT = format(new Date(), 'yyyy-MM-dd');

const { hideSalaryView } = useFeatureFlags();

const currentPage = ref(1);
const perPage = ref(SALARY_REPORT_PER_PAGE_OPTIONS[0]);
const filters = ref({ [FILTER_KEYS.STARTS_ON]: STARTS_ON_DEFAULT, [FILTER_KEYS.ENDS_ON]: ENDS_ON_DEFAULT });

const appliedApiFilters = computed(() => omitBy(pick(filters.value, API_FILTER_KEYS), value => isNil(value)));

// Endpoint 403s server-side for hidden-salary users; `enabled` keeps it from firing.
const failedSalaryQuery = useQuery({
  ...getFailedSalaryReport(appliedApiFilters),
  enabled: computed(() => !hideSalaryView.value),
});

const loading = computed(() => failedSalaryQuery.isFetching.value);

// Decorate each row with its days-in-failed count + escalation bucket; both drive sort/filter/export.
const rows = computed(() =>
  (failedSalaryQuery.data.value ?? []).map(row => {
    const days = daysInFailed(row.failed_at);
    return { ...row, days_in_failed: days, bucket: bucketFor(days) };
  }),
);

const selectedBuckets = computed(() => filters.value[FILTER_KEYS.BUCKET] || []);

// Filter options in fixed escalation order, not row order.
const BUCKET_ORDER = [
  FAILED_SALARY_BUCKET_LABELS.UNDER_TWO_WEEKS,
  FAILED_SALARY_BUCKET_LABELS.TWO_WEEKS,
  FAILED_SALARY_BUCKET_LABELS.THREE_WEEKS,
  FAILED_SALARY_BUCKET_LABELS.UNKNOWN,
];
const availableBuckets = computed(() => {
  const present = new Set(rows.value.map(row => row.bucket));
  return BUCKET_ORDER.filter(bucket => present.has(bucket));
});

const filteredRows = computed(() =>
  rows.value.filter(row => !selectedBuckets.value.length || selectedBuckets.value.includes(row.bucket)),
);

const filterItems = computed(() => [
  {
    key: FILTER_KEYS.STARTS_ON,
    label: 'From',
    type: CONTROL_TYPES.DATE,
    defaultValue: STARTS_ON_DEFAULT,
    controlProps: { max: filters.value[FILTER_KEYS.ENDS_ON] || null },
  },
  {
    key: FILTER_KEYS.ENDS_ON,
    label: 'To',
    type: CONTROL_TYPES.DATE,
    defaultValue: ENDS_ON_DEFAULT,
    controlProps: { min: filters.value[FILTER_KEYS.STARTS_ON] || null },
  },
  {
    key: FILTER_KEYS.BUCKET,
    label: 'Time in failed state',
    type: CONTROL_TYPES.CHECKBOX,
    options: availableBuckets.value.map(bucket => ({ text: bucket, value: bucket })),
    hidden: availableBuckets.value.length < 2,
  },
]);

const exportHeaders = computed(() => FAILED_SALARY_REPORT_FIELD_DEFINITIONS.map(field => field.label));

const exportFileRows = row =>
  FAILED_SALARY_REPORT_FIELD_DEFINITIONS.map(field =>
    field.formatter ? field.formatter(row[field.key], field.key, row) : row[field.key],
  );

// Reset to the first page whenever the result set changes (filter/date change).
watch(filteredRows, () => {
  currentPage.value = 1;
});
</script>
